import { Modal } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ImportKeysFailedDialog from "components/import-file-dialog/ImportKeysFailedDialog";
import SelectingView from "components/import-file-dialog/SelectingView";
import {
    DEVICE_STATUS_IMPORT_HEADERS,
    DeviceStatusImportFileError,
    MAX_IMPORT_ROWS,
    readDeviceStatusImportFile,
} from "components/rules/deviceStatusImport";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onImport: (records: Record<string, string>[]) => void;
}

type ImportState = { step: "selecting" } | { step: "failed"; message: string };

const SELECTING: ImportState = { step: "selecting" };

const downloadTemplate = (fileName: string) => {
    const url = URL.createObjectURL(new Blob([DEVICE_STATUS_IMPORT_HEADERS.join(",")], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

export const ImportDeviceValueRulesModal = (props: Props) => {
    const { t } = useTranslation();
    const [state, setState] = React.useState<ImportState>(SELECTING);
    const [isLoading, setIsLoading] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const onDownloadTemplateClicked = () => downloadTemplate(t("DeviceStatus.import.templateFileName"));

    const close = () => {
        setState(SELECTING);
        props.onClose();
    };

    const describeFailure = (error: unknown) =>
        error instanceof DeviceStatusImportFileError
            ? t(`DeviceStatus.import.error.${error.reason}`, { maxAllowed: MAX_IMPORT_ROWS })
            : t("DeviceStatus.import.error.readFailed");

    const importFile = (file: File | undefined) => {
        if (file === undefined) {
            return;
        }
        setIsLoading(true);
        readDeviceStatusImportFile(file)
            .then((records) => {
                setState(SELECTING);
                props.onImport(records);
            })
            .catch((error: unknown) => setState({ step: "failed", message: describeFailure(error) }))
            .finally(() => setIsLoading(false));
    };

    const renderContent = () => {
        switch (state.step) {
            case "failed":
                return (
                    <ImportKeysFailedDialog
                        failureMessage={t("AssetManagement.upload.fileUploadModal.error.importFailed", {
                            error: state.message,
                        })}
                        onUploadAnotherClicked={() => setState(SELECTING)}
                        onClose={close}
                        licenseKeysUpload={true}
                        onDownloadFileClicked={onDownloadTemplateClicked}
                    />
                );
            case "selecting":
                return (
                    <SelectingView
                        handleFileDrop={(fileList, event) => {
                            event.preventDefault();
                            importFile(fileList[0]);
                        }}
                        onFileInputChange={(event) => importFile(event.target.files?.[0])}
                        onSelectFileClicked={() => fileInputRef.current?.click()}
                        fileInputRef={fileInputRef}
                        licenseKeysUpload={true}
                        onDownloadFileClicked={onDownloadTemplateClicked}
                    />
                );
        }
    };

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={close}
            title={t("DeviceStatus.import.title")}
            isLoading={isLoading}
            loadingText={t("DeviceStatus.import.loading")}
        >
            {renderContent()}
        </Modal>
    );
};
